/**
 * IGenesysRenderer - common interface for Genesys renderers (WebGL and WebGPU).
 * Abstracts differences between THREE.WebGLRenderer and THREE.WebGPURenderer.
 */

import type * as THREE from 'three';
import type XRManager from 'three/src/renderers/common/XRManager.js';
import type { WebGPURenderer } from 'three/webgpu';

/** Renderer type selection */
export type RendererType = 'webgl' | 'webgpu' | 'auto';
export const DefaultRendererType: RendererType = 'auto';

/** Shadow map configuration for renderers */
export interface ShadowMapConfig {
  enabled: boolean;
  type: THREE.ShadowMapType | null;
}

/** Common interface for Genesys renderers (WebGL and WebGPU) */
export interface IGenesysRenderer {
  /** Canvas DOM element */
  readonly domElement: HTMLCanvasElement;

  /** WebXR manager for VR/AR support */
  readonly xr: XRManager | THREE.WebXRManager;

  /** Shadow map configuration */
  readonly shadowMap: ShadowMapConfig;

  /** Tone mapping mode */
  toneMapping: THREE.ToneMapping;

  /** Tone mapping exposure */
  toneMappingExposure: number;

  render(scene: THREE.Scene, camera: THREE.Camera): void;
  setSize(width: number, height: number): void;
  setPixelRatio(ratio: number): void;
  getPixelRatio(): number;
  setAnimationLoop(callback: ((time: number) => void) | null): void;
  dispose(): void;
  /**
   * WebGPU: releases renderer state for a torn-down scene (RenderObjects, internal
   * background mesh, node cache). App must dispose scene textures separately. No-op on WebGL.
   */
  disposeSceneResources(scene: THREE.Scene): void;
  beginRenderFrame(): void;
  endRenderFrame(): void;


  asWebGL(): THREE.WebGLRenderer | null;
  asWebGPU(): WebGPURenderer | null;
}

